import {
  CTA,
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  styled,
} from '@volkswagen-onehub/components-core';
import { useEffect } from 'react';
import Box from '../components/Box/Box';
import { useTribalContext } from '@/context/tribal';
import { convertBreakpointsToMediaQueries } from '../utils/themeUtils';
import { transformSizes } from '../components/Box/Box.styles';
import {
  performVWBasicButtonClick,
  performVWBasicErrorMessageLoad,
  performVWBasicFindDealerSearchClick,
  performVWBasicPageLoad,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { NAVIGATION_HEIGHT } from '../constants/navigation';
import useGlobalStore from '@/stores/globalStore';

const StyledBox = styled(Box)(
  ({ theme }) => `
  display: flex;
  height: calc(100vh - ${NAVIGATION_HEIGHT}px);
  width: 100vw;
  padding: 0 5% ${NAVIGATION_HEIGHT}px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    ${theme.colors.border.divider} 55%
  );
`);

const ButtonWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${theme.size.dynamic0050};
  
  ${convertBreakpointsToMediaQueries({
    b1280: transformSizes(theme, {
      gap: 'static150',
      flexDirection: 'row',
      justifyContent: 'center',
    }),
  })}
`
);

const ErrorContainer = styled(Box)(
  ({ theme }) => `
  text-align: center;
  margin-bottom:${theme.size.dynamic0130}
`
);

interface Props {
  error: any;
}

const Error = ({ error }: Props) => {
  const { contentManager, analyticsBuilder, analyticsManager } =
    useTribalContext();
  const { configurations, retailers } = useGlobalStore((state) => state.app);

  const browseSimilarVehiclesUrl = contentManager.get(
    'browse-similar-vehicles-href'
  );
  const browseSimilarVehiclesLabel = contentManager.get(
    'ctas-browse-similar-vehicles'
  );

  const findRetailerUrl = contentManager.get('find-retailer-href');
  const findRetailerLabel = contentManager.get('ctas-find-retailer');

  const analyticsCommonFields = {
    analyticsManager,
    analyticsBuilder,
    configurations,
    retailers,
  };

  performVWBasicPageLoad(analyticsCommonFields);

  useEffect(() => {
    performVWBasicErrorMessageLoad({
      ...analyticsCommonFields,
      error: {
        code: error.status,
        message: error.message,
      },
    });
  }, []);

  return (
    <StyledBox>
      <Box
        as={Text}
        mb={DesignTokenSizeEnum.dynamic0050}
        appearance={TokenTextAppearance.headline500}
        bold
      >
        {contentManager.get('loading-screen-error-title')}
      </Box>
      <ErrorContainer as={Text} appearance={TokenTextAppearance.headline200}>
        {contentManager.get('error-general')}
      </ErrorContainer>
      <ButtonWrapper>
        <CTA
          emphasis="primary"
          size="large"
          tag="a"
          href={browseSimilarVehiclesUrl}
          onClick={() =>
            performVWBasicButtonClick({
              label: browseSimilarVehiclesLabel,
              sectionName: SectionNames.ERROR_SCREEN,
              analyticsManager,
              analyticsBuilder,
            })
          }
        >
          {browseSimilarVehiclesLabel}
        </CTA>
        <CTA
          emphasis="secondary"
          size="large"
          tag="a"
          href={findRetailerUrl}
          target="_blank"
          onClick={() =>
            performVWBasicFindDealerSearchClick({
              label: findRetailerLabel,
              sectionName: SectionNames.ERROR_SCREEN,
              analyticsManager,
              analyticsBuilder,
            })
          }
        >
          {findRetailerLabel}
        </CTA>
      </ButtonWrapper>
    </StyledBox>
  );
};

export default Error;
